import { useQuery } from '@apollo/client'
import { Stack } from '@mui/material'
import ClientIdentityProvider from 'client/contexts/ClientIdentityContext'
import { GetRosterForClient } from 'client/graphql/search.gql'
import { SearchCandidateListRouteParams } from 'client/routes'
import { clientRouteConfig } from 'client/shared/routeTargets'
import { useNavigate, useParams } from 'react-router-dom'
import { CandidateSqlSearchProvider } from 'recruiter/contexts/CandidateSqlSearchContext'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getPxAsRem } from 'shared/utils'
import ClientRosterCardPreview from '../ClientRosterCardPreview'
import { ClientCandidateTable } from './ClientCandidateTable'
import { getRosterSearchPreviewProps } from './getRosterSearchPreviewProps'

const RosterSearchCandidateList: React.FC = () => {
    const navigate = useNavigate()
    const toast = useToast()
    const currentRosterId =
        useParams<SearchCandidateListRouteParams>().rosterId ?? ''
    const { data: rosterData, loading: rosterLoad } = useQuery(
        GetRosterForClient,
        {
            variables: { rosterId: currentRosterId ?? '' },
            skip: !currentRosterId,
            onError: () => {
                toast({
                    title: 'Error',
                    message: 'Unable to access search, please try again',
                    severity: 'error',
                })
                navigate(`${clientRouteConfig.searchesNew.path}`)
            },
        }
    )

    usePageTitle(rosterData?.getRosterForClient?.rosterName ?? undefined)

    return (
        <ClientIdentityProvider>
            <CandidateSqlSearchProvider rosterId={currentRosterId}>
                <Stack
                    direction="column"
                    flex={1}
                    minWidth={0}
                    height="100%"
                    padding={getPxAsRem(24)}
                    spacing={3}
                    overflow="hidden"
                >
                    {rosterLoad ? (
                        <LoadingPanel />
                    ) : (
                        <>
                            <ClientRosterCardPreview
                                {...getRosterSearchPreviewProps(rosterData)}
                            />
                            <ClientCandidateTable />
                        </>
                    )}
                </Stack>
            </CandidateSqlSearchProvider>
        </ClientIdentityProvider>
    )
}

export default RosterSearchCandidateList
